<template>
  <div class="mr" :class="{ 'mr--empty': isEmpty }" data-test="pfe-row" :data-row-key="row._key">
    <div class="mr-grid">
      <div class="mr-cell mr-cell--codes">
        <div class="mr-label-line">
          <input
            class="mr-label"
            type="text"
            :value="row.label || ''"
            placeholder="Add a group name"
            aria-label="Group name"
            maxlength="80"
            data-test="pfe-row-label"
            @input="onLabel($event.target.value)"
          />
          <span
            v-if="workflows.length"
            class="mr-warn"
            role="img"
            tabindex="0"
            :title="warnTip"
            :aria-label="warnTip"
            data-test="pfe-row-warn"
          ><i class="fa fa-exclamation-triangle" aria-hidden="true"></i></span>
        </div>
        <code-picker
          :value="codes"
          :options="codeOptions"
          :loading="codesLoading"
          :initial-open="autoOpenCodes"
          @input="onCodes"
        />
      </div>

      <div class="mr-cell">
        <form-picker
          kind="consent"
          :value="consentId"
          :forms="forms"
          :loading="formsLoading"
          :needs-review="reviewConsent"
          @input="onConsent"
        />
      </div>

      <div class="mr-cell">
        <form-picker
          kind="postOp"
          :value="postOpId"
          :forms="forms"
          :loading="formsLoading"
          :needs-review="reviewPostOp"
          @input="onPostOp"
        />
      </div>

      <div class="mr-cell mr-cell--send" :class="{ 'mr-cell--off': !postOpId }" :title="postOpId ? '' : 'Pick a post-op form to send it automatically'">
        <span class="mr-send-label">Auto-send</span>
        <label class="mr-check">
          <input type="checkbox" :checked="smsOn" :disabled="!postOpId" data-test="pfe-channel-sms" @change="onChannel('sms', $event.target.checked)" />
          <span>Text</span>
        </label>
        <label class="mr-check">
          <input type="checkbox" :checked="emailOn" :disabled="!postOpId" data-test="pfe-channel-email" @change="onChannel('email', $event.target.checked)" />
          <span>Email</span>
        </label>
      </div>

      <div class="mr-cell mr-cell--x">
        <button type="button" class="mr-remove" aria-label="Remove row" title="Remove row" data-test="pfe-row-remove" @click="$emit('remove')">
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </div>
    </div>

    <div v-if="note" class="mr-note" data-test="pfe-row-note">
      <i class="fa fa-info-circle" aria-hidden="true"></i>{{ note }}
    </div>
  </div>
</template>

<script>
import CodePicker from './CodePicker.vue';
import FormPicker from './FormPicker.vue';
import {
  consentFormID, postOpFormID, rowCodes, rowHasForm, rowNote, cellNeedsReview, isOn,
  withCodes, withConsentForm, withPostOpForm, withChannel, withLabel, overlapTooltip,
} from '@/utils/postOp/rows';

/**
 * One row of the Procedure mappings table. Presentational: every edit is built with the pure helpers in
 * utils/postOp/rows.js and emitted as a whole new row (`change`) — the editor owns the list.
 */
export default {
  name: 'PfeMappingRow',
  components: { CodePicker, FormPicker },
  props: {
    row: { type: Object, required: true },
    forms: { type: Array, default: () => [] },
    formsLoading: { type: Boolean, default: false },
    codeOptions: { type: Array, default: () => [] },
    codesLoading: { type: Boolean, default: false },
    /** Names of the active workflows that ALSO message this row's codes (the double-send warning). */
    workflows: { type: Array, default: () => [] },
    autoOpenCodes: { type: Boolean, default: false },
  },
  computed: {
    codes() { return rowCodes(this.row); },
    consentId() { return consentFormID(this.row); },
    postOpId() { return postOpFormID(this.row); },
    reviewConsent() { return cellNeedsReview(this.row, 'consent'); },
    reviewPostOp() { return cellNeedsReview(this.row, 'postOp'); },
    smsOn() { return !!this.postOpId && isOn(this.row.postOp.sms); },
    emailOn() { return !!this.postOpId && isOn(this.row.postOp.email); },
    isEmpty() { return !rowHasForm(this.row) && !this.reviewConsent && !this.reviewPostOp; },
    note() { return rowNote(this.row); },
    warnTip() { return overlapTooltip(this.workflows); },
  },
  methods: {
    onLabel(v) { this.$emit('change', withLabel(this.row, v)); },
    onCodes(codes) { this.$emit('change', withCodes(this.row, codes)); },
    onConsent(id) { this.$emit('change', withConsentForm(this.row, id)); },
    onPostOp(id) { this.$emit('change', withPostOpForm(this.row, id)); },
    onChannel(channel, on) { this.$emit('change', withChannel(this.row, channel, on)); },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.mr {
  padding: 14px 20px 16px;
  border-top: 1px solid $pp-border;
  background: $pp-surface;
  display: flex;
  flex-direction: column;
  gap: 10px;

  &:first-child { border-top: 0; }
  &--empty { background: $pfe-neutral-25; }
}
.mr-grid {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1.25fr) minmax(0, 1.25fr) 104px 36px;
  gap: 16px;
  align-items: start;
}
.mr-cell { min-width: 0; }

.mr-label-line {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 22px;
  margin-bottom: 5px;
}
.mr-label {
  width: 200px;
  max-width: 100%;
  min-width: 0;
  margin-left: -6px;
  padding: 2px 6px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  font: inherit;
  font-size: 12.5px;
  font-weight: 700;
  color: $pfe-neutral-600;

  &:hover { background: $pfe-neutral-50; }
  &:focus { outline: none; background: $pfe-neutral-50; color: $pp-text; }
  &::placeholder { font-weight: 500; color: $pfe-neutral-400; }
}
.mr-warn {
  display: inline-flex;
  color: #b77400;
  font-size: 13px;
  cursor: help;
}

.mr-cell--send {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding-top: 30px;

  &.mr-cell--off { opacity: 0.45; }
}
.mr-send-label {
  // the header row already says "Auto-send"; this label is only shown when the columns stack
  display: none;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $pfe-neutral-500;
}
.mr-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 14px;
  color: $pp-text;
  cursor: pointer;

  input { width: 16px; height: 16px; margin: 0; accent-color: $pp-brand; cursor: inherit; }
}
.mr-cell--x { padding-top: 30px; }
.mr-remove {
  width: 36px;
  height: 36px;
  border: 1px solid transparent;
  border-radius: 9px;
  background: transparent;
  color: $pfe-neutral-500;
  cursor: pointer;

  &:hover { background: $pp-red-subtle; color: $pp-red-text; border-color: $pp-red-border; }
  &:focus-visible { outline: 2px solid $pp-brand; outline-offset: 2px; }
}
.mr-note {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: $pfe-neutral-600;

  i { color: $pfe-neutral-400; }
}

// Tablet and below: the five columns stack into a card so nothing needs sideways scrolling.
@media (max-width: 900px) {
  .mr-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
  .mr-cell--codes { grid-column: 1 / -1; }
  .mr-cell--send { grid-column: 1; padding-top: 0; flex-direction: row; flex-wrap: wrap; align-items: center; gap: 14px; }
  .mr-send-label { display: inline; }
  .mr-cell--x { grid-column: 2; padding-top: 0; justify-self: end; }
}
@media (max-width: 560px) {
  .mr { padding: 14px 14px 16px; }
  .mr-grid { grid-template-columns: minmax(0, 1fr); }
  .mr-cell--x { grid-column: 1; }
}
</style>
